import { Stack, ThemeProvider, useTheme } from '@mui/material'
import { LngLatBoundsLike } from 'mapbox-gl'
import { FeatureCollection } from '@turf/turf'
import { MapboxGeoJSONFeature, MapLayerMouseEvent } from 'mapbox-gl'
import { FC, useCallback, useContext, useRef, ReactNode } from 'react'
import ReactMapGL, { MapProps, MapRef } from 'react-map-gl/dist/es5'
import { LayerInteractionContext, LayerInteractionProvider } from '../LayerInteractionContext'
import { MAPBOX_STYLES, MAPBOX_TOKEN, themeForMapStyle } from './const'
import { MapStyles } from './Layers'
import { useFitBoundsPadding } from './useFitBoundsPadding'

let hoverFeatures: MapboxGeoJSONFeature[] | undefined

const DISABLE_MAPS = import.meta.env.VITE_DISABLE_MAPS

export interface StaticMapGLProps extends Omit<MapProps, 'padding'> {
  defaultLayers?: boolean
  focusFeatures?: FeatureCollection
  bounds: LngLatBoundsLike
  padding?: number
  layers?: ReactNode
  mapStyle?: MapStyles
  overlay?: ReactNode
  ['data-testid']?: string
}

export const StaticMapGL: FC<StaticMapGLProps> = props => (
  <LayerInteractionProvider>
    <MapGLHoverable {...props} />
  </LayerInteractionProvider>
)

const MapGLHoverable: FC<StaticMapGLProps> = ({
  children,
  defaultLayers,
  focusFeatures,
  interactiveLayerIds,
  bounds,
  padding = 0,
  layers,
  mapStyle = MapStyles.FOCUSED,
  overlay,
  ...mapGLProps
}) => {
  const container = useRef<HTMLDivElement>(null)
  const mapRef = useRef<MapRef>(null)
  const theme = useTheme()

  const fitBoundsOptions = useFitBoundsPadding(padding)

  const { setHoverFeatures } = useContext(LayerInteractionContext)

  const handleMouseMove = useCallback(
    (e: MapLayerMouseEvent) => {
      if (mapRef.current) {
        //show something is interactable
        mapRef.current.getCanvas().style.cursor = e.features?.length ? 'pointer' : ''

        if (hoverFeatures) {
          hoverFeatures.forEach(f => mapRef.current?.setFeatureState(f, { hover: false }))
        }

        if (e.features) {
          hoverFeatures = e.features
          e.features.forEach(f => mapRef.current?.setFeatureState(f, { hover: true }))

          setHoverFeatures(e.features)
        }
      }
      //@ts-ignore mapbox event handler types do not match react-map-gl event handler types
      mapGLProps.onMouseMove && mapGLProps.onMouseMove(e)
    },
    [interactiveLayerIds, mapRef.current, mapGLProps.onMouseMove]
  )

  return (
    <Stack sx={{ flex: 1 }} direction="row" ref={container} data-testid={mapGLProps['data-testid']}>
      <ThemeProvider theme={themeForMapStyle(theme, mapStyle)}>
        <ReactMapGL
          ref={mapRef}
          interactive={false}
          initialViewState={{
            bounds,
            fitBoundsOptions,
          }}
          //@ts-ignore
          onMouseMove={handleMouseMove}
          interactiveLayerIds={interactiveLayerIds}
          style={{ flex: 1 }}
          //@ts-ignore
          mapStyle={
            (mapStyle && MAPBOX_STYLES[mapStyle][theme.palette.mode]) || MAPBOX_STYLES.FOCUSED.light
          }
          mapboxAccessToken={!DISABLE_MAPS && MAPBOX_TOKEN}
          testMode={DISABLE_MAPS}
          attributionControl={false}
          {...mapGLProps}
          reuseMaps
        >
          {children}
        </ReactMapGL>
      </ThemeProvider>
    </Stack>
  )
}
